import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Die Seite zu "Fotobox unter 100 €".
 *
 * Sie bekommt 127 Klicks bei 2.453 Impressionen — die höchste Klickrate
 * aller Kategorieseiten. Menschen klicken, weil sie das Versprechen
 * glauben. Im Katalog stehen dafür genau zwei Pakete, beide ohne Drucker.
 *
 * Diese Seite sagt das, statt es zu verstecken. Wer nach 99 € sucht und
 * eine ehrliche Antwort bekommt, springt seltener ab als jemand, der erst
 * nach drei Klicks merkt, dass es das Angebot nicht gibt.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-6 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function GuenstigInhalt({ analyse, stand }) {
  if (!analyse) return null;

  const { drunter, knappDrueber, guenstigstesMitDruck } = analyse;

  return (
    <div className="space-y-12 lg:space-y-16">
      <Abschnitt
        id="was-gibt-es"
        titel={`Was es wirklich für unter ${analyse.grenze} € gibt`}
        untertitel="Die ehrliche Antwort vorweg — es sind wenige."
      >
        <p>
          Von {analyse.anzahlPakete} Paketen im Vergleich liegen{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.anzahlDrunter}
          </strong>{" "}
          unter {analyse.grenze} €
          {analyse.mitDruckDrunter === 0
            ? " — und keines davon druckt."
            : `, davon ${analyse.mitDruckDrunter} mit Drucker.`}{" "}
          Die Hälfte aller Angebote liegt bei {euro(analyse.medianAlle)} €.
        </p>

        {drunter.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[420px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <th scope="col" className="py-2 pr-4 font-semibold">Fotobox</th>
                  <th scope="col" className="py-2 pr-4 font-semibold">Lieferung</th>
                  <th scope="col" className="py-2 pr-4 font-semibold">Druck</th>
                  <th scope="col" className="py-2 text-right font-semibold">Preis</th>
                </tr>
              </thead>
              <tbody>
                {drunter.map((a) => (
                  <tr key={`${a.box}-${a.paket}`} className="border-b border-slate-100 last:border-0">
                    <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                      {a.box}
                    </th>
                    <td className="py-2.5 pr-4 text-slate-600">
                      {a.versand ? "per Versand" : "vor Ort"}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-600">
                      {a.druckt ? "ja" : "nur digital"}
                    </td>
                    <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                      {euro(a.preis)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6">
          Beide verzichten auf den Drucker. Das ist kein Mangel, sondern die
          Rechnung, die den Preis möglich macht: Drucker, Papier und Farbband
          sind der teuerste Teil einer Fotobox. Ohne sie bekommt ihr eine
          vollwertige Box mit Kamera, Licht und Bildschirm — die Bilder landen
          in einer Online-Galerie statt in der Hand.
        </p>
      </Abschnitt>

      <Abschnitt
        id="lohnt-der-aufpreis"
        titel="Lohnt sich der Schritt zum Drucker?"
        untertitel="Was zwischen digital und gedruckt liegt."
      >
        {guenstigstesMitDruck && (
          <p>
            Das günstigste Paket mit Drucker kostet{" "}
            <strong className="font-semibold text-slate-900">
              {euro(guenstigstesMitDruck.preis)} €
            </strong>{" "}
            ({guenstigstesMitDruck.box}). Zwischen der digitalen Box für{" "}
            {euro(drunter[0]?.preis || 0)} € und dem ersten Druckpaket liegen
            also rund {euro(guenstigstesMitDruck.preis - (drunter[0]?.preis || 0))} €.
          </p>
        )}
        <p className="mt-4">
          Ob sich das lohnt, hängt an einer einzigen Frage:{" "}
          <strong className="font-semibold text-slate-900">
            Sollen die Gäste etwas mitnehmen?
          </strong>{" "}
          Wer ein Fotobox-Gästebuch führt, in das die Ausdrucke geklebt werden,
          braucht den Drucker zwingend. Für eine Party, bei der die Bilder
          abends in der Gruppe geteilt werden, ist die digitale Box die
          vernünftigere Wahl — und spart genug für Getränke.
        </p>

        {knappDrueber.length > 0 && (
          <>
            <h3 className="mt-8 text-lg font-semibold text-slate-900">
              Knapp über {analyse.grenze} €
            </h3>
            <p className="mt-2">
              {analyse.anzahlKnappDrueber} weitere Pakete liegen zwischen{" "}
              {analyse.grenze} und {Math.round(analyse.grenze * 1.5)} €. Wer
              beim Budget etwas Luft hat, findet dort deutlich mehr Auswahl:
            </p>
            <ul className="mt-3 space-y-1.5">
              {knappDrueber.map((a) => (
                <li key={`${a.box}-${a.paket}`} className="flex justify-between gap-4 border-b border-slate-100 py-1.5 last:border-0">
                  <span className="text-slate-700">
                    {a.box}
                    <span className="text-slate-500">
                      {" "}
                      · {a.druckt ? "mit Druck" : "digital"}
                    </span>
                  </span>
                  <span className="font-semibold tabular-nums text-slate-900">
                    {euro(a.preis)} €
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Abschnitt>

      <Abschnitt
        id="wirklich-guenstig"
        titel="Woran ihr ein wirklich günstiges Angebot erkennt"
        untertitel="Der niedrigste Preis ist nicht immer der niedrigste Preis."
      >
        <ul className="list-disc space-y-2.5 pl-5 marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">
              Ist die Lieferung dabei?
            </strong>{" "}
            Bei Versandboxen ist sie es fast immer. Bei Anbietern vor Ort kommt
            oft eine Anfahrtspauschale dazu — im Vergleich rechnen wir sie für
            deine Postleitzahl aus.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Wie lange gilt der Preis?
            </strong>{" "}
            Manche Angebote gelten für vier Stunden, andere für den ganzen Tag.
            Bei einer Hochzeit, die um 15 Uhr beginnt, ist das ein Unterschied.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Was kostet der Nachschub?
            </strong>{" "}
            Ein günstiges Druckpaket mit 100 Bildern kann teurer werden als
            eine Flatrate, wenn ihr nachbestellen müsst.
          </li>
        </ul>
        <p className="mt-6">
          Im{" "}
          <Link
            href="/fotobox-vergleich"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Vergleich
          </Link>{" "}
          steht bei jedem Angebot der Gesamtpreis — Grundpreis plus Paket, nicht
          der Lockpreis.
        </p>
        <p className="mt-6 text-xs text-slate-500">
          Alle Preise aus dem Katalog, Stand {stand}.
        </p>
      </Abschnitt>
    </div>
  );
}
